import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw, Save, GitCompare, Download, Clock, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

interface CapTableTimelineProps {
  companyId: string;
}

export function CapTableTimeline({ companyId }: CapTableTimelineProps) {
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [taking, setTaking] = useState(false);
  const [label, setLabel] = useState('');
  const [reason, setReason] = useState('Manual');
  const [left, setLeft] = useState('');
  const [right, setRight] = useState('');
  const [diffs, setDiffs] = useState<any[]>([]);
  const [snapshotRows, setSnapshotRows] = useState<any[]>([]);
  const [active, setActive] = useState('');

  async function load() {
    setLoading(true);
    const { data, error } = await supabase.rpc('list_snapshots', { company_id: companyId });
    if (error) toast.error(error.message);
    setSnapshots(data || []);
    setLoading(false);
  }

  async function takeSnapshot() {
    if (!label) return toast.error('Add a label for this snapshot');
    setTaking(true);
    const { error } = await supabase.functions.invoke('take-snapshot', {
      body: { companyId, label, reason },
    });
    if (error) toast.error(error.message);
    else {
      toast.success('Snapshot taken');
      setLabel('');
      setReason('Manual');
      load();
    }
    setTaking(false);
  }

  async function viewSnapshot(id: string) {
    setActive(id);
    const { data, error } = await supabase.rpc('get_snapshot', {
      company_id: companyId,
      snapshot_id: id,
    });
    if (error) return toast.error(error.message);
    const snapshot = Array.isArray(data) && data.length > 0 ? data[0].snapshot : [];
    setSnapshotRows(Array.isArray(snapshot) ? snapshot : []);
    setDiffs([]);
  }

  async function compare() {
    if (!left || !right) return toast.error('Choose two snapshots to compare');
    const { data, error } = await supabase.functions.invoke('diff-snapshots', {
      body: { companyId, leftId: left, rightId: right },
    });
    if (error) return toast.error(error.message);
    setDiffs(data || []);
  }

  async function exportPdf() {
    const { data, error } = await supabase.functions.invoke('snapshot-export-pdf', {
      body: { companyId, snapshotId: active, diffs: diffs.length ? diffs : null },
    });
    if (error) toast.error(error.message);
    else if (data?.url) window.open(data.url, '_blank');
  }

  async function deleteSnapshot(id: string, label: string) {
    if (!confirm(`Delete snapshot "${label}"?`)) return;

    const { error } = await supabase
      .from('cap_table_snapshots')
      .delete()
      .eq('id', id)
      .eq('company_id', companyId);

    if (error) {
      toast.error('Failed to delete: ' + error.message);
    } else {
      toast.success('Snapshot deleted');
      if (active === id) {
        setActive('');
        setSnapshotRows([]);
        setDiffs([]);
      }
      load();
    }
  }

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" /> Cap Table Timeline
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Take Snapshot */}
        <div className="grid sm:grid-cols-4 gap-2">
          <Input
            placeholder="Snapshot Label (e.g., 'Pre-Seed Close')"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Input
            placeholder="Reason (e.g., 'Round Closed')"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Button onClick={takeSnapshot} disabled={taking}>
            {taking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Take Snapshot
          </Button>
          <Button variant="outline" onClick={exportPdf} disabled={!active}>
            <Download className="h-4 w-4 mr-1" /> Export PDF
          </Button>
        </div>

        {/* Timeline list */}
        {loading ? (
          <div className="p-6 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {snapshots.map((s) => (
                <TableRow key={s.id} className={active === s.id ? 'bg-muted/50' : ''}>
                  <TableCell>{new Date(s.taken_at).toLocaleString()}</TableCell>
                  <TableCell>{s.label}</TableCell>
                  <TableCell>{s.reason}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center gap-2 justify-end">
                      <Button size="sm" variant="outline" onClick={() => viewSnapshot(s.id)}>
                        View
                      </Button>
                      <Select value={left} onValueChange={setLeft}>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Left" />
                        </SelectTrigger>
                        <SelectContent>
                          {snapshots.map((x) => (
                            <SelectItem key={x.id} value={x.id}>
                              {x.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={right} onValueChange={setRight}>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue placeholder="Right" />
                        </SelectTrigger>
                        <SelectContent>
                          {snapshots.map((x) => (
                            <SelectItem key={x.id} value={x.id}>
                              {x.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button size="sm" onClick={compare}>
                        <GitCompare className="h-4 w-4 mr-1" /> Compare
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => deleteSnapshot(s.id, s.label)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Snapshot table */}
        {active && !diffs.length && (
          <Card className="border border-dashed">
            <CardHeader>
              <CardTitle>Snapshot Details</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Holder</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead className="text-right">Shares</TableHead>
                    <TableHead className="text-right">Ownership %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(snapshotRows || []).map((r: any) => (
                    <TableRow key={`${r.holder}-${r.class}`}>
                      <TableCell>{r.holder}</TableCell>
                      <TableCell>{r.class}</TableCell>
                      <TableCell className="text-right">
                        {Number(r.shares).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {(Number(r.pct) * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Diff table */}
        {diffs.length > 0 && (
          <Card className="border border-dashed">
            <CardHeader>
              <CardTitle>Diff (Left → Right)</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Holder</TableHead>
                    <TableHead>Class</TableHead>
                    <TableHead className="text-right">Left %</TableHead>
                    <TableHead className="text-right">Right %</TableHead>
                    <TableHead className="text-right">Δ %</TableHead>
                    <TableHead className="text-right">Δ Shares</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {diffs.map((d: any) => (
                    <TableRow key={`${d.holder}-${d.class}`}>
                      <TableCell>{d.holder}</TableCell>
                      <TableCell>{d.class}</TableCell>
                      <TableCell className="text-right">
                        {(Number(d.pct_left || 0) * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {(Number(d.pct_right || 0) * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {(Number(d.delta_pct || 0) * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell className="text-right">
                        {Number(d.delta_shares || 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}
